import { lazy, Suspense, ComponentType, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface UseMDXContentOptions {
  fallback?: ReactNode
}

/**
 * Hook to dynamically load language-specific MDX content
 *
 * @example
 * // Automatically loads from content/{language}/index.mdx
 * const Content = useMDXContent({ fallback: <LoadingSpinner /> });
 * return <Content />;
 */
export function useMDXContent(options: UseMDXContentOptions = {}): ComponentType {
  const { i18n } = useTranslation()
  const { fallback } = options

  const language = i18n.language || 'en'

  // Dynamically import the MDX file from language-specific content directory
  const MDXComponent = lazy(() =>
    import(`../content/landing/${language}/index.mdx`)
      .catch(() => {
        // Fallback to English if language-specific content doesn't exist
        console.warn(`MDX file content/landing/${language}/index.mdx not found, falling back to English`)
        return import(`../content/landing/en/index.mdx`)
      })
      .catch(error => {
        // Final fallback error
        console.error(`Failed to load content: ${error.message}`)
        throw error
      })
  )

  // Wrap in Suspense with optional fallback
  return () => (
    <Suspense fallback={fallback || <div>Loading...</div>}>
      <MDXComponent />
    </Suspense>
  )
}
